<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="isEdit ? 'Update Notification Template' : 'Create Notification Template'"
    ok-title="Save"
    cancel-title="Cancel"
    @show="resetModal"
    @hidden="onHidden"
    @ok="handleOk"
    centered
    size="lg"
  >
    <b-form ref="form" @submit.stop.prevent="handleSubmit">
      <b-form-group
        label="Name"
        label-for="name-input"
        :state="getState('name')"
        invalid-feedback="Name is required and must be less than 255 characters."
      >
        <b-form-input
          id="name-input"
          v-model="form.name"
          :state="getState('name')"
          placeholder="Name"
          required
          maxlength="255"
        />
      </b-form-group>

      <b-form-group
        label="Subject"
        label-for="subject-input"
        :state="getState('subject')"
        invalid-feedback="Subject is required."
      >
        <AutoSuggestionTextArea
          id="subject-input"
          v-model="form.subject"
          :suggestions="variableSuggestions"
          :rows="2"
          placeholder="Subject"
        />
      </b-form-group>

      <b-form-group label="Body" label-for="body-input">
        <AutoSuggestionTextArea
          id="body-input"
          v-if="data?.channel_code !== 'email'"
          v-model="form.body"
          :suggestions="variableSuggestions"
          :rows="14"
          placeholder="Body"
        />

        <AutoSuggestionEditor
          v-else
          id="body-input"
          v-model="form.body"
          :suggestions="variableSuggestions"
          placeholder="Tulis konten email..."
        />
      </b-form-group>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">Cancel</b-button>
      <b-button variant="info" @click="onPreview" :disabled="!form.body">Preview</b-button>
      <b-button variant="primary" @click="ok()">Save</b-button>
    </template>
  </b-modal>
</template>

<script>
import AutoSuggestionEditor from "@/components/AutoSuggestionEditor.vue";
import AutoSuggestionTextArea from "@/components/AutoSuggestionTextArea.vue";

export default {
  name: "NotificationTemplateModal",

  components: { AutoSuggestionTextArea, AutoSuggestionEditor },

  props: {
    modalId: { type: String, default: "notification-template-modal" },
    isEdit: { type: Boolean, default: false },
    data: { type: Object, default: null },
  },

  data() {
    return {
      form: this.getInitialForm(),
      formErrors: {},
      suppressValidation: false,
    };
  },

  watch: {
    isEdit(val) {
      if (!val) {
        this.form = this.getInitialForm();
        this.formErrors = {};
      } else {
        if (this.data) this.populateFromData(this.data);
      }
    },

    data: {
      deep: true,
      handler(val) {
        if (this.isEdit && val) {
          this.populateFromData(val);
        }
      },
    },

    "form.name"(val) {
      if (this.suppressValidation) return;
      if (!val) this.formErrors.name = "Name is required.";
      else if (val.length > 255) this.formErrors.name = "Name must be less than 255 characters.";
      else delete this.formErrors.name;
    },

    "form.subject"(val) {
      if (this.suppressValidation) return;
      if (!val) this.formErrors.subject = "Subject is required.";
      else delete this.formErrors.subject;
    },
  },

  computed: {
    variableSuggestions() {
      if (this.data?.event?.default_variables) {
        return Object.keys(this.data.event.default_variables).map((key) => `{{${key}}}`);
      }
      return [];
    },
  },

  methods: {
    getInitialForm() {
      return {
        name: "",
        subject: "",
        body: "",
      };
    },

    populateFromData(d) {
      this.form = {
        name: d.name || "",
        subject: d.subject || "",
        body: d.body || "",
      };

      this.formErrors = {};
    },

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.name) this.formErrors.name = "Name is required.";
      else if (this.form.name.length > 255) this.formErrors.name = "Name must be less than 255 characters.";

      if (!this.form.subject) this.formErrors.subject = "Subject is required.";

      return Object.keys(this.formErrors).length === 0;
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    handleSubmit() {
      const isValid = this.validateAll();
      if (!isValid) return;

      const payload = { ...this.form };
      this.$emit("save", payload);
    },

    async resetModal() {
      this.suppressValidation = true;

      await this.$nextTick();

      if (this.isEdit && this.data) {
        this.populateFromData(this.data);
      } else {
        this.form = this.getInitialForm();
        this.formErrors = {};
      }

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    onPreview() {
      this.$emit("preview", {
        id: this.data?.id,
        body: this.form.body,
      });
    },
  },
};
</script>

<style scoped>
.w-50 {
  width: 50%;
}
</style>
